import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useCart, SHIPPING_OPTIONS, ShippingLocation, CartItem } from '@/context/CartContext';
import { Minus, Plus, Trash2, ShoppingBag, MapPin, ChevronDown, ArrowLeft, AlertCircle } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const LocationSelector = () => {
  const { shippingLocation, setShippingLocation, freeShipping } = useCart();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = SHIPPING_OPTIONS.find(o => o.value === shippingLocation);

  return (
    <div ref={ref} className="relative mb-4">
      <label className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5" />
        Delivery Location
      </label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border border-border bg-muted/30 hover:bg-muted/50 transition-colors text-sm"
      >
        <span className={selected ? 'text-foreground' : 'text-muted-foreground'}>
          {selected ? selected.label : 'Select your location'}
        </span>
        <div className="flex items-center gap-2">
          {selected && !freeShipping && (
            <span className="text-xs font-medium text-primary">₪{selected.fee}</span>
          )}
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1.5 w-full rounded-xl border border-border bg-background shadow-lg overflow-hidden animate-fade-in">
          {SHIPPING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setShippingLocation(option.value as ShippingLocation);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-colors hover:bg-muted/50 ${
                shippingLocation === option.value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
              }`}
            >
              <span>{option.label}</span>
              <span className={`text-xs font-medium ${
                shippingLocation === option.value ? 'text-primary' : 'text-muted-foreground'
              }`}>
                {freeShipping ? (
                  <span className="text-green-500">Free</span>
                ) : (
                  `₪${option.fee}`
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AnimatedCheckmark = () => (
  <div className="relative w-20 h-20 mx-auto mb-6">
    <svg className="w-20 h-20" viewBox="0 0 80 80">
      <circle cx="40" cy="40" r="36" fill="none" stroke="hsl(142, 71%, 45%)" strokeWidth="3" strokeLinecap="round"
        style={{ strokeDasharray: 226, strokeDashoffset: 226, animation: 'circle-draw 0.6s ease-out forwards' }} />
      <path d="M24 42 L35 53 L56 29" fill="none" stroke="hsl(142, 71%, 45%)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ strokeDasharray: 60, strokeDashoffset: 60, animation: 'check-draw 0.4s ease-out 0.5s forwards' }} />
    </svg>
    <style>{`
      @keyframes circle-draw { to { stroke-dashoffset: 0; } }
      @keyframes check-draw { to { stroke-dashoffset: 0; } }
    `}</style>
  </div>
);

type CheckoutPhase = 'summary' | 'transitioning' | 'form' | 'confirmed';

const CartSidebar = () => {
  const {
    items, isOpen, totalItems, subtotal, shippingFee, total,
    freeShipping, canCheckout, shippingLocation, allVersionsSelected,
    toggleCart, removeItem, updateQuantity, updateItemVersion, clearCart,
  } = useCart();

  const [phase, setPhase] = useState<CheckoutPhase>('summary');
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', address: '' });

  const locationLabel = SHIPPING_OPTIONS.find(o => o.value === shippingLocation)?.label ?? '';

  // Reset phase when cart closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setPhase('summary');
        setForm({ firstName: '', lastName: '', phone: '', address: '' });
      }, 300);
    }
  }, [isOpen]);

  const handlePlaceOrder = () => {
    setPhase('transitioning');
    setTimeout(() => setPhase('form'), 400);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.phone.trim() || !form.address.trim()) return;
    setPhase('confirmed');
  };

  const handleDone = () => {
    clearCart();
    setPhase('summary');
    setForm({ firstName: '', lastName: '', phone: '', address: '' });
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => toggleCart(open)}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col bg-background border-l border-border">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-foreground">
            <ShoppingBag className="w-5 h-5" />
            Your Cart {totalItems > 0 && `(${totalItems})`}
          </SheetTitle>
        </SheetHeader>

        {/* Confirmation overlay */}
        {phase === 'confirmed' ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-6 animate-fade-in">
            <AnimatedCheckmark />
            <h3 className="text-xl font-bold text-foreground mb-2">Thank You!</h3>
            <p className="text-muted-foreground mb-1">Your order has been placed, {form.firstName}!</p>
            <p className="text-sm text-muted-foreground mb-6">
              We'll contact you at {form.phone} to confirm delivery to {locationLabel}.
              <br />Payment is cash on delivery.
            </p>
            <div className="w-full bg-muted/30 rounded-xl p-4 mb-6 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Items</span>
                <span className="text-foreground">{items.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                <span className="text-foreground">{freeShipping ? 'Free' : `₪${shippingFee.toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between font-bold text-base pt-1.5 border-t border-border">
                <span>Total</span>
                <span>₪{total.toFixed(2)}</span>
              </div>
            </div>
            <button onClick={handleDone}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm tracking-wider uppercase hover:opacity-90 transition-opacity">
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Items list */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <ShoppingBag className="w-16 h-16 mb-4 opacity-20" />
                  <p className="text-lg font-medium">Your cart is empty</p>
                  <p className="text-sm mt-1">Add some products to get started</p>
                </div>
              ) : (
                items.map((item) => (
                  <div key={item.id} className="flex gap-4 p-3 rounded-xl bg-muted/50">
                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {item.productImage ? (
                        <img src={item.productImage} alt={item.name} className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-2xl font-display text-muted-foreground/30">{item.subtitle.charAt(0)}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-sm text-foreground truncate">{item.brand} {item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.subtitle}
                            {item.version && (
                              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-md bg-primary/10 text-primary text-[10px] font-semibold">
                                {item.version}
                              </span>
                            )}
                          </p>
                        </div>
                        <button onClick={() => removeItem(item.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Version selector for items with multiple versions */}
                      {item.availableVersions && item.availableVersions.length > 1 && (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-1.5">
                            {item.availableVersions.map((v) => {
                              const vPrice = item.versionPrices?.[v];
                              return (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() => updateItemVersion(item.id, v, vPrice)}
                                  className={`px-2 py-1.5 rounded-lg text-[11px] font-semibold transition-all border ${
                                    item.version === v
                                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                                      : 'bg-muted/50 text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                                  }`}
                                >
                                  {v}{vPrice ? ` · ₪${vPrice}` : ''}
                                </button>
                              );
                            })}
                          </div>
                          {!item.version && (
                            <p className="text-[10px] text-amber-500 mt-1 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Please select a size
                            </p>
                          )}
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-2 bg-background rounded-lg border border-border">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-1.5 hover:bg-muted transition-colors rounded-l-lg">
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-1.5 hover:bg-muted transition-colors rounded-r-lg">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="font-medium text-sm text-foreground">₪{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer area — animated transitions */}
            {items.length > 0 && (
              <div className="border-t border-border pt-4 relative overflow-hidden">
                {/* Summary phase — slides up and fades out */}
                <div
                  className="transition-all duration-400 ease-out"
                  style={{
                    transform: phase === 'transitioning' || phase === 'form' ? 'translateY(-30px)' : 'translateY(0)',
                    opacity: phase === 'transitioning' || phase === 'form' ? 0 : 1,
                    maxHeight: phase === 'form' ? 0 : '500px',
                    overflow: 'hidden',
                    transitionDuration: phase === 'form' ? '0ms' : '400ms',
                    pointerEvents: phase !== 'summary' ? 'none' : 'auto',
                    position: phase === 'form' ? 'absolute' : 'relative',
                  }}
                >
                  <div className="space-y-3">
                    <LocationSelector />
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="text-foreground">₪{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Shipping</span>
                      <span className="text-foreground">
                        {!shippingLocation ? (
                          <span className="text-muted-foreground text-xs">Select location</span>
                        ) : freeShipping ? (
                          <span className="text-green-500">Free</span>
                        ) : (
                          `₪${shippingFee.toFixed(2)}`
                        )}
                      </span>
                    </div>
                    {!freeShipping && subtotal > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Add ₪{(120 - subtotal).toFixed(2)} more for free shipping
                      </p>
                    )}
                    <div className="flex justify-between text-base font-bold pt-2 border-t border-border">
                      <span className="text-foreground">Total</span>
                      <span className="text-foreground">₪{total.toFixed(2)}</span>
                    </div>
                    {subtotal < 80 && (
                      <p className="text-xs text-amber-500 text-center">
                        Minimum order is ₪80. Add ₪{(80 - subtotal).toFixed(2)} more.
                      </p>
                    )}
                    {subtotal >= 80 && !shippingLocation && (
                      <p className="text-xs text-amber-500 text-center">
                        Please select a delivery location to continue.
                      </p>
                    )}
                    {subtotal >= 80 && shippingLocation && !allVersionsSelected && (
                      <p className="text-xs text-amber-500 text-center flex items-center justify-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Please select a version for all products above.
                      </p>
                    )}
                    <button
                      disabled={!canCheckout}
                      onClick={handlePlaceOrder}
                      className="w-full py-3 rounded-xl font-bold text-sm tracking-wider uppercase transition-all duration-300 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Place Order — Cash on Delivery
                    </button>
                    <p className="text-[11px] text-center text-muted-foreground">
                      Payment is always cash upon delivery
                    </p>
                  </div>
                </div>

                {/* Form phase — fades in */}
                {phase === 'form' && (
                  <div className="animate-fade-in">
                    <button
                      onClick={() => setPhase('summary')}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>

                    <h3 className="text-lg font-bold text-foreground mb-1">Delivery Details</h3>
                    <p className="text-xs text-muted-foreground mb-4">
                      Delivering to <span className="font-medium text-foreground">{locationLabel}</span> • Cash on delivery
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="text-xs font-medium text-foreground mb-1 block">First Name *</label>
                          <input type="text" required value={form.firstName}
                            onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-muted/30 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all placeholder:text-muted-foreground"
                            placeholder="First name" />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs font-medium text-foreground mb-1 block">Last Name</label>
                          <input type="text" value={form.lastName}
                            onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                            className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-muted/30 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all placeholder:text-muted-foreground"
                            placeholder="Optional" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-foreground mb-1 block">Phone Number *</label>
                        <input type="tel" required value={form.phone}
                          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                          className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-muted/30 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all placeholder:text-muted-foreground"
                          placeholder="05X-XXX-XXXX" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-foreground mb-1 block">Delivery Address *</label>
                        <textarea required value={form.address}
                          onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                          className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-muted/30 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all resize-none placeholder:text-muted-foreground"
                          rows={2} placeholder="City, street, building, apartment..." />
                      </div>

                      <div className="border-t border-border pt-3 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Subtotal</span>
                          <span className="text-foreground">₪{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Shipping to {locationLabel}</span>
                          <span className="text-foreground">{freeShipping ? <span className="text-green-500">Free</span> : `₪${shippingFee.toFixed(2)}`}</span>
                        </div>
                        <div className="flex justify-between font-bold pt-2 border-t border-border">
                          <span>Total</span>
                          <span>₪{total.toFixed(2)}</span>
                        </div>
                        <button type="submit"
                          className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm tracking-wider uppercase hover:opacity-90 transition-all">
                          Confirm Order
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSidebar;
